import { useCallback, useEffect } from 'react';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { ModalSliceType, openModal } from '../../redux/slices/modal';

export default function useNavigationBlocker(
  analysisId: string,
  shouldBlock: boolean,
  message: string = 'You have unsaved changes. Are you sure you want to leave?',
) {
  const dispatch = useAppDispatch();
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    },
    [shouldBlock, message],
  );

  // Add the event listener when the component mounts
  // this is to prevent the user from leaving the page without saving using browser history
  useEffect(() => {
    if (shouldBlock) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, handleBeforeUnload]);

  const blocker = useBlocker(shouldBlock);

  // This effect runs when the blocker state changes.
  // If the navigation is blocked and the user confirms the message, it proceeds with the navigation.
  useEffect(() => {
    if (blocker.state === 'blocked') {
      dispatch(
        openModal({
          type: ModalSliceType.SaveConfirmModal,
          data: {
            analysisId,
            onOk: () => blocker.proceed(),
            onCancel: () => blocker.reset(),
          },
          id: analysisId,
        }),
      );
    }
  }, [blocker, message, analysisId, dispatch]);
}
